import { Button } from "@/lib/components/Button/Button";
import CreditsText from "@/features/Billing/CreditsText";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/lib/components/Tooltip";
import { IconInfoCircle } from "@tabler/icons-react";
import { useState } from "react";
import { useParams } from "react-router";
import {
  CreateVideoRenderDto,
  VideoRenderDto,
  VideoRenderExportType,
  VideoRenderUpscaleQuality,
} from "@repo/api/video-render";
import {
  CREDITS_VIDEO_RENDER_UPSCALE_PER_SECOND,
  calculateVideoRenderUpscaleCredits,
} from "@repo/api/utils/billing";
import {
  placeholderVideoRenderName,
  safeFilename,
} from "@repo/api/utils/download";
import { getCompositionDurationMs } from "@repo/video-engine-i2v/utils/timing";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { useTeamQuery } from "@/lib/queries/team-queries";
import { cn } from "@/lib/utils";
import { RadioIndicator } from "@/lib/components/RadioGroup";
import { ExportVideoDialogState } from "./ExportVideoDialog";
import { getOption } from "./ExportOption";
import { estimateRenderTime } from "./export-video-utils";
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/lib/components/Dialog";

type Props = {
  exportType: VideoRenderExportType;
  createVideoRender: (data: CreateVideoRenderDto) => void;
  isCreatingVideoRender: boolean;
  videoName: string;
  setDialogState: (state: ExportVideoDialogState) => void;
  videoRenders: VideoRenderDto[] | undefined;
};

type QualityOption = {
  label: string;
  sublabel?: string;
  quality: VideoRenderUpscaleQuality | null;
};

export const EXPORT_QUALITY_OPTIONS: QualityOption[] = [
  { label: "1080p", quality: null },
  { label: "1080p", sublabel: "enhanced", quality: "1080p" },
  { label: "4K", quality: "4k" },
];

function getPerSecondLabel(quality: VideoRenderUpscaleQuality | null): string {
  if (quality === null) {
    return "Free";
  }
  return `${CREDITS_VIDEO_RENDER_UPSCALE_PER_SECOND[quality]}/sec`;
}

export default function ExportQualityView({
  exportType,
  createVideoRender,
  isCreatingVideoRender,
  videoName,
  setDialogState,
  videoRenders,
}: Props) {
  const { teamExternalId } = useParams();
  const { data: team } = useTeamQuery(teamExternalId!);
  const thirdPartyEditorInfo = useEditorStore(
    (state) => state.thirdPartyEditorInfo,
  );
  const composition = useEditorStore((state) => state.video?.compositionJson);
  const animatedImages = useEditorStore((state) => state.animatedImages);

  const [selectedQuality, setSelectedQuality] =
    useState<VideoRenderUpscaleQuality | null>(null);

  const option = getOption(exportType, thirdPartyEditorInfo);

  const hasEarlierExports =
    videoRenders?.some((render) => render.status === "done") ?? false;

  const videoDurationMs = composition
    ? getCompositionDurationMs(composition, animatedImages)
    : 0;

  const totalCredits =
    selectedQuality === null
      ? 0
      : calculateVideoRenderUpscaleCredits(videoDurationMs, selectedQuality);

  const estimatedMinutes = estimateRenderTime(videoDurationMs, selectedQuality);

  return (
    <div className="flex h-full flex-1 flex-col">
      <DialogHeader
        variant="bordered"
        onBack={() => setDialogState({ view: "EXPORT" })}
        showCloseButton={true}
      >
        <DialogTitle>{option.label}</DialogTitle>
        <DialogDescription className="sr-only">
          {option.label}
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col items-center px-4 pt-8 md:px-8">
        <div className="flex items-center gap-1.5 text-base font-semibold">
          Video quality
          <Tooltip delayDuration={0}>
            <TooltipTrigger className="text-text-secondary outline-none">
              <IconInfoCircle className="h-4 w-4" />
            </TooltipTrigger>
            <TooltipContent className="max-w-96 text-pretty">
              <p>
                <b>1080p</b> exports exactly what you see in the preview.
              </p>
              <br />
              <p>
                <b>1080p (enhanced)</b> and <b>4K</b> both use an AI enhancer
                for sharper detail. <b>4K</b> also upscales resolution.
              </p>
              <br />
              <p>
                AI enhancement is priced per second, based on the full video
                length
              </p>
            </TooltipContent>
          </Tooltip>
        </div>

        <div className="mt-3 mb-10 flex w-full flex-col items-center justify-center gap-4 sm:flex-row md:mb-0">
          {EXPORT_QUALITY_OPTIONS.map((qualityOption) => {
            const isSelected = qualityOption.quality === selectedQuality;

            return (
              <ExportQualityOption
                key={qualityOption.quality ?? "default"}
                qualityOption={qualityOption}
                isSelected={isSelected}
                setSelectedQuality={setSelectedQuality}
              />
            );
          })}
        </div>
      </div>

      <DialogFooter
        className="mt-10 mb-6 flex flex-col items-center px-4 md:px-8"
        variant={null}
      >
        <div className="flex flex-col items-center gap-3">
          <Button
            size="sm"
            onClick={() => {
              createVideoRender({
                externalFileName: safeFilename(
                  videoName || placeholderVideoRenderName(),
                ),
                exportType,
                upscaleQuality: selectedQuality,
              });
            }}
            isLoading={isCreatingVideoRender}
            disabled={isCreatingVideoRender}
            className="min-w-60"
          >
            <div className="flex items-center">
              <span>{option.label}</span>
              {totalCredits > 0 && (
                <CreditsText
                  className="min-w-12"
                  credits={totalCredits}
                  isVisible={
                    (team?.billingInfo.showCredits ?? false) &&
                    (team?.billingInfo.creditBalance ?? null) !== null
                  }
                />
              )}
            </div>
          </Button>
          <p className="text-xs text-text-secondary">
            Estimated rendering time: {estimatedMinutes}min
          </p>
        </div>
        {exportType === "LINK" && hasEarlierExports && (
          <p className="mt-5 text-xs text-text-secondary">
            Want to share a previous export?{" "}
            <button
              type="button"
              onClick={() => setDialogState({ view: "EARLIER_EXPORTS" })}
              className="cursor-pointer text-text-primary underline"
            >
              View earlier exports
            </button>
          </p>
        )}
      </DialogFooter>
    </div>
  );
}

function ExportQualityOption({
  qualityOption,
  isSelected,
  setSelectedQuality,
}: {
  qualityOption: QualityOption;
  isSelected: boolean;
  setSelectedQuality: (quality: VideoRenderUpscaleQuality | null) => void;
}) {
  const labelClassName = isSelected
    ? "bg-linear-to-r from-primary-400 to-secondary-400 bg-clip-text text-transparent"
    : "text-text-secondary";

  return (
    <button
      key={qualityOption.quality ?? "default"}
      onClick={() => setSelectedQuality(qualityOption.quality)}
      className={cn(
        "relative flex h-34 w-34 cursor-pointer flex-col items-center justify-center rounded-lg border transition-colors",
        isSelected
          ? "border-transparent"
          : "border-grey-200 bg-white hover:bg-button-outline-hover",
      )}
      style={
        isSelected
          ? {
              background:
                "linear-gradient(to bottom right, var(--color-primary-100), var(--color-secondary-100)) padding-box, linear-gradient(to bottom right, var(--color-primary-400), var(--color-secondary-400)) border-box",
            }
          : undefined
      }
    >
      <RadioIndicator checked={isSelected} className="absolute top-3 left-3" />
      <div className="relative flex items-center justify-center">
        <span
          className={cn("text-lg leading-none font-semibold", labelClassName)}
        >
          {qualityOption.label}
        </span>
        {qualityOption.sublabel && (
          <span className={cn("absolute top-full text-xs", labelClassName)}>
            ({qualityOption.sublabel})
          </span>
        )}
      </div>
      <div
        className={cn(
          "absolute bottom-3 flex items-center gap-0.5 text-xs font-semibold",
          isSelected ? "text-text-primary" : "text-text-secondary",
        )}
      >
        <img src="/CreditIcon.svg" alt="" className="mb-0.5 h-3 w-3" />
        <span>{getPerSecondLabel(qualityOption.quality)}</span>
      </div>
    </button>
  );
}
